<template>
  <div
    :class="{
      'content-summary-snippet': true,
      'content-summary-snippet--hide-decorations': props.hideDecorations,
    }"
    style="position: relative"
  >
    <!--<div class="content-summary-snippet__delete-button-wrap">
      <CircleWithIconBtn
        v-if="props.isUnbindButtonVisible"
        class="content-summary-snippet__delete-button"
        iconName="scissors_3"
        iconColor="rgba(180, 80, 80, 1)"
        iconColorHover="rgba(180, 80, 80, 1)"
        color="white"
        :iconSize="16"
        size="big"
        @click="emit('clickDelete')"
      />
    </div>

    <div v-if="props.isRemoveButtonVisible" style="position: absolute; top: 7px; right: 5px; z-index: 10">
      <LinkWithIcon isNoUnderline @click="emit('clickRemove')">
        <template #icon>
          <IconEmbedded name="remove_2" :size="17" />
        </template>
      </LinkWithIcon>
    </div>-->

    <div v-if="props.isContentSourcedFromLabelVisible" class="content-summary-snippet__label-row">
      <div class="content-summary-snippet__title">
        {{ contentSourceLabel }}
      </div>
      <InfoIconWithTooltip v-if="isMatchRationaleVisible" class="content-summary-snippet__rationale-icon" style="position: relative; top: 1px">
        <b>Why this content?</b><br />{{ props.matchRationale }}
      </InfoIconWithTooltip>
    </div>

    <!-- Creator visit summary -->
    <div class="creator-visit-summary content-summary-snippet__content">
      <template v-for="(contentSummary, index) in contentSummaries" :key="contentSummary.id">
        <div class="creator-visit-summary__row">
          <div class="creator-visit-summary__title-wrap">
            <span class="global-h4 creator-visit-summary__title">{{ contentSummary.contextName }}</span>
            <LinkWithIcon tag="RouterLink" :to="getContextSourceLink(contentSummary)" target="_blank" isNoUnderline color="light-gray" style="margin-left: 7px; position: relative; top: 2px">
              <template #icon><IconEmbedded name="external-link_2" :size="17" /></template>
            </LinkWithIcon>
          </div>

          <!--<div class="creator-visit-summary__thumbnails-row">-->
          <!--  <div class="creator-visit-summary__thumbnails-row-in-wrap">-->
          <!--    <SrpFileThumbnail-->
          <!--      class="creator-visit-summary__thumbnail"-->
          <!--      v-for="fileName in contentSummary.examples"-->
          <!--      :key="fileName"-->
          <!--      :fileName="fileName"-->
          <!--      :isFixedAspectRatio="false"-->
          <!--      :isWithLoader="false"-->
          <!--    />-->
          <!--  </div>-->
          <!--</div>-->

          <div class="creator-visit-summary__assets-row">
            <LinkWithIcon class="creator-visit-summary__assets-count-link" isDottedUnderline @click="emit('clickViewPhotos', contentSummary)">
              <!--<template #icon><IconEmbedded name="image_2" :size="17" /></template>-->
              <span>{{ getAssetCountLabel(contentSummary) }}</span>
            </LinkWithIcon>
            <div v-if="getFormattedContentDate(contentSummary)" class="creator-visit-summary__date-wrap">
              <span class="creator-visit-summary__date-separator" />
              <span class="creator-visit-summary__content-date">{{ getFormattedContentDate(contentSummary) }}</span>
            </div>
          </div>
        </div>
        <div v-if="index < contentSummaries.length - 1" class="creator-visit-summary__divider"></div>
      </template>
    </div>
    <!-- / Creator visit summary -->
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Types
import { ContentSummaryPreview } from "@contracts/contentCalendar";

// Components
// import CircleWithIconBtn from "@components/ui/CircleWithIconBtn.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";

const props = withDefaults(
  defineProps<{
    contentSummaryData?: ContentSummaryPreview | null;
    contentSummaryDataList?: Array<ContentSummaryPreview> | null;
    matchRationale?: string | null;
    customerId: string;
    isContentSourcedFromLabelVisible?: boolean;
    hideDecorations?: boolean;
    showVideoCount?: boolean;
  }>(),
  {
    matchRationale: null,
    contentSummaryData: null,
    contentSummaryDataList: null,
    isContentSourcedFromLabelVisible: true,
    hideDecorations: false,
    showVideoCount: false,
  }
);

const emit = defineEmits<{
  (e: "clickViewPhotos", contentSummary?: ContentSummaryPreview): void;
}>();

const contentSummaries = computed<Array<ContentSummaryPreview>>(() => {
  if (props.contentSummaryDataList?.length) {
    return props.contentSummaryDataList;
  }
  return props.contentSummaryData ? [props.contentSummaryData] : [];
});

const contentSourceLabel = computed<string>(() => {
  const count = contentSummaries.value.length;
  return `Matched ${count} Content Source${count === 1 ? "" : "s"}`;
});

const isMatchRationaleVisible = computed<boolean>(() => Boolean(props.matchRationale && !props.matchRationale.startsWith("LocationExternalId")));

function getAssetCountLabel(contentSummary: ContentSummaryPreview): string {
  if (props.showVideoCount) {
    const videoCount = contentSummary.videoCount ?? 0;
    const photoCount = contentSummary.photoCount ?? 0;
    const photoLabel = `${photoCount} Photo${photoCount === 1 ? "" : "s"}`;
    if (videoCount === 0) {
      return photoLabel;
    }
    return `${photoLabel}, ${videoCount} video${videoCount === 1 ? "" : "s"}`;
  }
  return `${contentSummary.assetCount} Photo${contentSummary.assetCount > 1 ? "s" : ""}`;
}

// Generates the Context Source Link ==========================================
function getContextSourceLink(contentSummaryData: ContentSummaryPreview): object {
  return {
    CreatorVisit: {
      name: "PaidCollabCommunityAdmin",
      params: { communityId: props.customerId },
      hash: `#collab=${contentSummaryData.contextId}`,
    },
    CommunityUpload: {
      name: "CommunityPhotos",
      params: { pageId: props.customerId },
      hash: `#group=community-upload&sectionId=${contentSummaryData.contextId}`,
    },
    CustomerUpload: {
      name: "CommunityPhotos",
      params: { pageId: props.customerId },
      hash: `#group=my-uploads&sectionId=${contentSummaryData.contextId}`,
    },
    Location: {
      name: "CommunityPhotos",
      params: { pageId: props.customerId },
      hash: `#group=location&sectionId=${contentSummaryData.contextId}`,
    },
  }[contentSummaryData.contextType];
}

// Formatted content date (uses contentDate if present, falls back to lastProcessed)
function getFormattedContentDate(contentSummaryData: ContentSummaryPreview): string | null {
  const dateStr = contentSummaryData.contentDate ?? contentSummaryData.lastProcessed;
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
</script>

<style scoped lang="scss">
@import "./contentSummarySnippet.scss";
@import "@/scss/variables.scss";

.content-summary-snippet {
  &__label-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    position: absolute;
    inset: 9px auto auto 51px;
    z-index: 2;
  }

  &__rationale-icon {
    display: inline-flex;
    background: rgba(255, 255, 255, 1);
    border-radius: 100px;
  }

  &__label-row &__title {
    position: static;
    inset: auto;
  }
}

// Creator visit summary ======================================================
.creator-visit-summary {
  &__row {
  }

  &__divider {
    height: 1px;
    margin: 14px 0;
    background: rgba(0, 0, 0, 0.12);
  }

  &__title-wrap {
    margin-bottom: 9px;
  }

  &__title {
    font-weight: 500;
  }

  &__thumbnails-row {
    width: calc(100% + 10px);
    height: 35px;
    margin-bottom: 9px;
    position: relative;
    z-index: 0;
    overflow: hidden;
    mask-image: linear-gradient(90deg, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 95%);
  }

  &__thumbnails-row-in-wrap {
    width: 1000px;
    height: 35px;
    gap: 4px;
    display: flex;
    position: absolute;
    inset: 0 auto auto 0;
  }

  &__thumbnail {
    height: 35px;
    border-radius: 3px;

    :deep(*) {
      border-radius: 3px !important;
    }
  }

  &__assets-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 4px;
  }

  &__assets-count-link {
    @include standardFont;
    // margin-left: -4px;
    white-space: nowrap;
  }

  &__date-wrap {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  &__date-separator {
    width: 1px;
    height: 14px;
    margin: 0 8px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 1px;
    flex-shrink: 0;
  }

  &__content-date {
    @include standardFont;
    white-space: nowrap;
  }
}
</style>
